<script setup lang="ts">
/**
 * 代理商公司（`/resellers`，superadmin 專屬）——批93-C §2.2。
 *
 * 這一頁管的是**公司**，不是帳號：先在這裡把公司與它的產品範圖建好，才能到〈帳號管理〉
 * 開一個 `role='reseller'` 的帳號掛上去（後端不變式：`role==='reseller'` ⇔ `reseller_id` 非 null）。
 *
 * 三個容易踩的點都寫在畫面上：
 * - **產品範圖不可留空**（後端 POST 強制非空）：留空的語意會變成「不限」，之後上線的新產品
 *   就靜默對所有代理商開放了——這種洩漏沒有人會發現。
 * - **公司停權＝底下所有帳號當場 403**（`require_reseller` 每次請求都檢公司 active），
 *   不必逐一停用帳號。
 * - **沒有刪除**：公司 id 是客戶／授權／申請的 FK 快照來源，刪掉會斷歷史。要收掉就停權。
 */
import { computed, onMounted, ref } from 'vue'

import UiButton from '@/components/UiButton.vue'
import UiModal from '@/components/UiModal.vue'
import { ApiError } from '@/api/client'
import { contractBadge } from '@/lib/contract'
import * as ls from '@/api/ls'
import type { Reseller, ResellerInput } from '@/api/ls'
import { useDataStore } from '@/stores/data'
import { useDialogsStore } from '@/stores/dialogs'
import { vCardTable } from '@/lib/cardTable'

const store = useDataStore()
const dialogs = useDialogsStore()

const rows = ref<Reseller[]>([])
const loading = ref(true)
const loadError = ref('')

async function load() {
  loading.value = true
  try {
    rows.value = await ls.listResellers()
    loadError.value = ''
  } catch (e) {
    loadError.value = errText(e, '讀取代理商清單失敗')
  } finally {
    loading.value = false
  }
}

onMounted(async () => {
  // 產品範圖的勾選清單來自憑證設計（`GET /products`），與核發框同一份來源。
  await Promise.all([store.ensureProducts().catch(() => {}), load()])
  // 批107：負責業務下拉。本頁是 superadmin 專屬，打得到 `GET /admin-users`；
  // 只留**啟用中的我方員工**（代理商角色掛上去語意相反，後端也會 422）。
  try {
    const users = await ls.listAdminUsers()
    staffOptions.value = users
      .filter((u) => u.active && u.role !== 'reseller')
      .map((u) => ({ id: u.id, email: u.email, name: u.name }))
  } catch {
    staffOptions.value = []
  }
})

function errText(e: unknown, fallback: string): string {
  const d = e instanceof ApiError && e.payload ? (e.payload as { detail?: unknown }).detail : undefined
  if (d == null) return fallback
  if (typeof d === 'string') return d
  const obj = d as { code?: string; message?: string }
  if (obj.message) return obj.message
  if (obj.code === 'code_taken') return '這個代號已經被別家代理商用了，請換一個。'
  return JSON.stringify(d)
}

// --- 新增／編輯（同一個對話框）---------------------------------------------

const open = ref(false)
/** 編輯中的既有列；null＝新增。 */
const editing = ref<Reseller | null>(null)
const busy = ref(false)
const form = ref<{
  name: string
  code: string
  contact_name: string
  contact_email: string
  contact_phone: string
  notes: string
  active: boolean
  // --- 批107：公司基本資料與合約 ---
  tax_id: string
  address: string
  territory: string
  company_phone: string
  website: string
  contract_starts_on: string
  contract_ends_on: string
  account_manager_id: string
}>({
  name: '', code: '', contact_name: '', contact_email: '', contact_phone: '', notes: '',
  active: true, tax_id: '', address: '', territory: '', company_phone: '', website: '',
  contract_starts_on: '', contract_ends_on: '', account_manager_id: '',
})
/** 負責業務的候選：**啟用中的我方員工**（後端同一條判準，選不到的組合後端也會 422）。 */
const staffOptions = ref<{ id: string; email: string; name: string | null }[]>([])
/** 勾選的產品 key；**空陣列＝不合法**（後端 422），不是「不限」。 */
const pickedProducts = ref<string[]>([])

function openCreate() {
  editing.value = null
  form.value = {
    name: '',
    code: '',
    contact_name: '',
    contact_email: '',
    contact_phone: '',
    notes: '',
    active: true,
    tax_id: '',
    address: '',
    territory: '',
    company_phone: '',
    website: '',
    contract_starts_on: '',
    contract_ends_on: '',
    account_manager_id: '',
  }
  pickedProducts.value = []
  open.value = true
}
function openEdit(r: Reseller) {
  editing.value = r
  form.value = {
    name: r.name,
    code: r.code ?? '',
    contact_name: r.contact_name ?? '',
    contact_email: r.contact_email ?? '',
    contact_phone: r.contact_phone ?? '',
    notes: r.notes ?? '',
    active: r.active,
    tax_id: r.tax_id ?? '',
    address: r.address ?? '',
    territory: r.territory ?? '',
    company_phone: r.company_phone ?? '',
    website: r.website ?? '',
    contract_starts_on: r.contract_starts_on ?? '',
    contract_ends_on: r.contract_ends_on ?? '',
    account_manager_id: r.account_manager_id ?? '',
  }
  // null（不限）在 UI 上呈現成「全部勾起來」——存回去時就變成顯式清單，這正是我們要的
  // （明確列出來的範圖，比一個會隨新產品自動擴張的 null 安全）。
  pickedProducts.value = r.product_keys ? [...r.product_keys] : store.products.map((p) => p.key)
  open.value = true
}
function toggleProduct(key: string) {
  pickedProducts.value = pickedProducts.value.includes(key)
    ? pickedProducts.value.filter((k) => k !== key)
    : [...pickedProducts.value, key]
}

const canSave = computed(() => !!form.value.name.trim() && pickedProducts.value.length > 0 && !busy.value)

async function save() {
  if (!canSave.value) return
  const body: ResellerInput = {
    name: form.value.name.trim(),
    code: form.value.code.trim() || null,
    contact_name: form.value.contact_name.trim() || null,
    contact_email: form.value.contact_email.trim() || null,
    contact_phone: form.value.contact_phone.trim() || null,
    product_keys: [...pickedProducts.value],
    notes: form.value.notes.trim() || null,
    active: form.value.active,
    // 批107：空字串一律送 null＝清空（後端對 date 欄位收不了空字串）。
    tax_id: form.value.tax_id.trim() || null,
    address: form.value.address.trim() || null,
    territory: form.value.territory.trim() || null,
    company_phone: form.value.company_phone.trim() || null,
    website: form.value.website.trim() || null,
    contract_starts_on: form.value.contract_starts_on || null,
    contract_ends_on: form.value.contract_ends_on || null,
    account_manager_id: form.value.account_manager_id || null,
  }
  busy.value = true
  try {
    if (editing.value) await ls.updateReseller(editing.value.id, body)
    else await ls.createReseller(body)
    open.value = false
    await load()
    // 客戶頁的「代理商」欄／篩選共用 store 那份快取——改完要讓它重載。
    await store.loadResellers()
  } catch (e) {
    await dialogs.notify({ title: '儲存失敗', message: errText(e, '儲存代理商失敗') })
  } finally {
    busy.value = false
  }
}

async function toggleActive(r: Reseller) {
  const next = !r.active
  if (!next) {
    const ok = await dialogs.confirm({
      title: '停權代理商',
      message: `停權「${r.name}」？該公司底下所有後台帳號會「當場」無法使用（每次請求都會檢查公司狀態），已核發的授權不受影響。`,
      danger: true,
      confirmText: '停權',
    })
    if (!ok) return
  }
  try {
    await ls.updateReseller(r.id, { name: r.name, active: next })
    await load()
    await store.loadResellers()
  } catch (e) {
    await dialogs.notify({ title: '操作失敗', message: errText(e, '變更狀態失敗') })
  }
}

/** 產品範圖的顯示文字（null＝不限；用產品中文名，查不到才印 key）。 */
function scopeText(r: Reseller): string {
  if (!r.product_keys) return '不限（全部產品）'
  if (!r.product_keys.length) return '（無）'
  return r.product_keys.map((k) => store.productName(k)).join('、')
}
function fmtDate(iso: string | null): string {
  return iso ? iso.slice(0, 10) : '—'
}
</script>

<template>
  <div class="page">
    <div class="page-head">
      <h1>代理商</h1>
      <span class="count">{{ rows.length }}</span>
      <span class="spacer" />
      <RouterLink class="tab" :to="{ name: 'admin-users' }">帳號管理</RouterLink>
      <UiButton variant="primary" @click="openCreate">＋ 新增代理商</UiButton>
    </div>

    <p class="subtle intro">
      代理商可以建立<strong>自家客戶</strong>、為他們開 portal 帳號、並提出授權申請（草稿 → 確認送出）；
      <strong>審核與撤銷永遠在我方手上</strong>。哪些申請可以由代理商免審核發，由〈產品 → 套裝〉每一列的
      「核發政策」決定，不是這裡。建好公司之後，到〈帳號管理〉新增一個「代理商」角色的帳號並掛上公司。
    </p>

    <div v-if="loading" class="loading-wrap"><span class="spinner lg" /></div>

    <div v-else class="card">
      <p v-if="loadError" class="err">{{ loadError }}</p>
      <div v-else-if="!rows.length" class="empty">
        <div class="empty-title">還沒有代理商</div>
        <div class="empty-sub">按右上角「新增代理商」建立第一家，記得同時圈定它可以賣的產品。</div>
      </div>
      <div v-else class="tbl-scroll" data-hscroll>
        <table v-card-table class="tbl card-table">
          <thead>
            <tr>
              <th>公司</th><th>代號</th><th>轄區</th><th>聯絡窗口</th><th>產品範圖</th>
              <th>合約</th><th>負責業務</th><th>狀態</th><th>建立</th><th></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="r in rows" :key="r.id" :class="{ dim: !r.active }">
              <td>
                <strong>{{ r.name }}</strong>
                <div v-if="r.notes" class="subtle note">{{ r.notes }}</div>
              </td>
              <td><code v-if="r.code" class="mono">{{ r.code }}</code><span v-else class="subtle">—</span></td>
              <!-- 批107：轄區是自由文字（主人裁示不做值域）。 -->
              <td class="nowrap">{{ r.territory || '—' }}</td>
              <td class="contact">
                <div>{{ r.contact_name ?? '—' }}</div>
                <div v-if="r.contact_email" class="subtle">{{ r.contact_email }}</div>
                <div v-if="r.contact_phone" class="subtle">{{ r.contact_phone }}</div>
              </td>
              <td class="scope">{{ scopeText(r) }}</td>
              <!-- 合約是純資訊：到期不停權，只提醒。徽章判定在 `lib/contract.ts`。 -->
              <td class="nowrap">
                <template v-if="r.contract_ends_on">
                  {{ r.contract_ends_on }}
                  <span
                    v-if="contractBadge(r.contract_ends_on).label"
                    class="c-badge"
                    :class="contractBadge(r.contract_ends_on).tone"
                  >
                    {{ contractBadge(r.contract_ends_on).label }}
                  </span>
                </template>
                <span v-else class="subtle">—</span>
              </td>
              <td class="nowrap">
                <template v-if="r.account_manager">
                  {{ r.account_manager.name || r.account_manager.email }}
                  <span v-if="!r.account_manager.active" class="subtle">（已停用）</span>
                </template>
                <span v-else class="subtle">—</span>
              </td>
              <td>
                <span class="status-pill" :class="r.active ? 'active' : 'revoked'">
                  {{ r.active ? '啟用中' : '已停權' }}
                </span>
              </td>
              <td class="nowrap">{{ fmtDate(r.created_at) }}</td>
              <td class="acts-cell">
                <div class="acts">
                  <UiButton variant="secondary" size="sm" @click="openEdit(r)">編輯</UiButton>
                  <UiButton
                    :variant="r.active ? 'warn' : 'secondary'"
                    size="sm"
                    @click="toggleActive(r)"
                  >
                    {{ r.active ? '停權' : '恢復' }}
                  </UiButton>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <UiModal
      :open="open"
      width="640px"
      :title="editing ? `編輯代理商 — ${editing.name}` : '新增代理商'"
      @close="open = false"
    >
      <div class="form">
        <label class="field"><span>公司名稱</span><input v-model="form.name" maxlength="120" /></label>
        <label class="field">
          <span>代號（選填）</span>
          <input v-model="form.code" maxlength="32" placeholder="人工對帳用，可留空" />
        </label>
        <label class="field"><span>聯絡人</span><input v-model="form.contact_name" maxlength="80" /></label>
        <label class="field">
          <span>聯絡 Email</span>
          <input v-model="form.contact_email" maxlength="160" placeholder="someone@example.com" />
        </label>
        <!-- 24＝後端 `admin.py::ResellerIn.contact_phone` 的 max_length（前端放寬只會讓人打完才被 422） -->
        <label class="field"><span>聯絡電話</span><input v-model="form.contact_phone" maxlength="24" /></label>
      </div>

      <!-- 批107：公司基本資料與合約。**我方營運資料**——客戶 portal 與代理商自己都看不到。 -->
      <h4 class="sub-title">公司資料</h4>
      <div class="form">
        <label class="field">
          <span>統一編號</span>
          <input v-model="form.tax_id" maxlength="16" placeholder="8 碼；海外代理商留空" />
        </label>
        <label class="field">
          <span>業務轄區</span>
          <input v-model="form.territory" maxlength="60" placeholder="例如：北區、大中華" />
        </label>
        <label class="field">
          <span>公司總機</span>
          <input v-model="form.company_phone" maxlength="24" placeholder="與窗口電話分開" />
        </label>
        <label class="field">
          <span>網站</span>
          <input v-model="form.website" maxlength="255" placeholder="https://" />
        </label>
        <label class="field addr-field">
          <span>公司地址</span>
          <input v-model="form.address" maxlength="255" />
        </label>
      </div>

      <h4 class="sub-title">代理合約</h4>
      <p class="subtle">
        <strong>純資訊</strong>：到期不會停權、不影響任何權限，只在清單顯示提醒徽章。
        要停權請用下面的「啟用中」。
      </p>
      <div class="form">
        <label class="field"><span>合約起日</span><input v-model="form.contract_starts_on" type="date" /></label>
        <label class="field"><span>合約迄日</span><input v-model="form.contract_ends_on" type="date" /></label>
        <label class="field">
          <span>我方負責業務</span>
          <select v-model="form.account_manager_id">
            <option value="">（未指定）</option>
            <option v-for="u in staffOptions" :key="u.id" :value="u.id">
              {{ u.name ? `${u.name}（${u.email}）` : u.email }}
            </option>
          </select>
        </label>
      </div>

      <h4 class="sub-title">產品範圖</h4>
      <p class="subtle">
        這家代理商能賣、能看、能申請的產品。<strong>不可留空</strong>——留空的語意是「不限」，
        之後上線的新產品會靜默對它開放。範圖之外的產品在它的畫面上完全不存在（清單空、寫入 403）。
      </p>
      <div class="prod-picks">
        <label
          v-for="p in store.products"
          :key="p.key"
          class="pick"
          :class="{ on: pickedProducts.includes(p.key) }"
        >
          <input type="checkbox" :checked="pickedProducts.includes(p.key)" @change="toggleProduct(p.key)" />
          {{ p.name }}<small v-if="p.status !== 'active'" class="dep">（已停售）</small>
        </label>
        <p v-if="!store.products.length" class="subtle">
          還沒有任何產品——請先到〈系統 → 產品〉建立一個。
        </p>
      </div>
      <p v-if="!pickedProducts.length" class="warn-text">⚠ 請至少勾選一個產品。</p>

      <label class="field notes-field">
        <span>備註（選填）</span>
        <textarea v-model="form.notes" rows="2" maxlength="500" />
      </label>
      <label v-if="editing" class="chk">
        <input v-model="form.active" type="checkbox" />
        啟用中
        <span class="subtle">取消勾選＝停權；該公司所有後台帳號會當場無法使用</span>
      </label>

      <template #foot>
        <UiButton @click="open = false">取消</UiButton>
        <UiButton variant="primary" :disabled="!canSave" :loading="busy" @click="save">
          {{ editing ? '儲存' : '建立' }}
        </UiButton>
      </template>
    </UiModal>
  </div>
</template>

<style scoped>
.page-head {
  display: flex;
  align-items: center;
  gap: var(--sp-2);
  margin-bottom: var(--sp-4);
}
.page-head h1 {
  margin: 0;
  font-size: var(--fs-20);
  color: var(--fg);
}
.count {
  font-size: var(--fs-12);
  color: var(--fg-3);
  background: var(--bg);
  padding: 1px 8px;
  border-radius: 999px;
}
.spacer {
  flex: 1;
}
.intro {
  margin: 0 0 var(--sp-3);
  line-height: var(--lh-base);
}
.acts {
  display: flex;
  gap: 6px;
  align-items: center;
}
.nowrap {
  white-space: nowrap;
}
tr.dim td {
  opacity: 0.6;
}
.note {
  margin-top: 2px;
  font-size: var(--fs-11);
}
.contact div {
  line-height: 1.4;
}
.scope {
  max-width: 280px;
  font-size: var(--fs-12);
  color: var(--fg-2);
}
.err {
  color: var(--danger);
  font-size: var(--fs-13);
}
/* 批97：原本是 flex-wrap＋`.field{min-width:180px;flex:1}`——640px 的對話框第一列硬塞三欄、
   每欄只剩約 191px，比輸入框的內在寬還窄，於是「公司名稱／代號／聯絡人」三格互相重疊、
   modal 長出左右卷軸（主人回報）。改成與客戶頁相同的兩欄 grid（`CustomersView .form`）。 */
.form {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--sp-3);
}
@media (max-width: 700px) {
  .form {
    grid-template-columns: 1fr;
  }
}
.field span {
  font-size: var(--fs-12);
  color: var(--fg-2);
}
.sub-title {
  margin: var(--sp-4) 0 var(--sp-1);
  font-size: var(--fs-13);
  color: var(--fg-3);
  font-weight: 600;
}
.prod-picks {
  display: flex;
  flex-wrap: wrap;
  gap: var(--sp-2);
  margin-top: var(--sp-2);
}
/* 膠囊式勾選（與 ModulePicker 同一套視覺，免得同一個 console 兩種多選長相） */
.pick {
  display: inline-flex;
  align-items: center;
  padding: 3px 11px;
  border: 1px solid var(--border-strong);
  border-radius: 999px;
  background: var(--bg);
  color: var(--fg-2);
  font-size: var(--fs-12);
  cursor: pointer;
  user-select: none;
}
.pick:hover {
  border-color: var(--brand-400);
}
.pick input {
  position: absolute;
  opacity: 0;
  width: 0;
  height: 0;
  pointer-events: none;
}
.pick.on {
  background: var(--brand-50);
  border-color: var(--brand-400);
  color: var(--brand-700);
  font-weight: 600;
}
.pick .dep {
  color: var(--fg-4);
}
.notes-field {
  display: grid;
  gap: 4px;
  margin-top: var(--sp-3);
}
.notes-field textarea {
  font-family: inherit;
  font-size: var(--fs-13);
  padding: 8px 10px;
  border: 1px solid var(--border-strong);
  border-radius: var(--r-sm);
  background: var(--bg-panel);
  color: var(--fg);
  resize: vertical;
}
.chk {
  display: flex;
  align-items: center;
  gap: var(--sp-2);
  margin-top: var(--sp-3);
  font-size: var(--fs-13);
  color: var(--fg-2);
}
/* ★ 不可叫 `.warn`：scoped 樣式會套到子元件根節點（UiButton 的 variant="warn"）。 */
.warn-text {
  margin: var(--sp-2) 0 0;
  color: var(--warning);
  font-size: var(--fs-12);
}
/* --- 批107：合約徽章 --- */
.c-badge {
  display: inline-block;
  margin-left: 6px;
  padding: 1px 7px;
  border-radius: 999px;
  font-size: var(--fs-12);
  font-weight: 600;
}
.c-badge.soon {
  background: var(--st-pending-bg);
  color: var(--st-pending-fg);
}
.c-badge.expired {
  background: var(--st-closed-bg);
  color: var(--st-closed-fg);
}
/* 公司地址橫跨兩欄（其餘欄位是兩欄 grid）。 */
.addr-field {
  grid-column: 1 / -1;
}
</style>
